import { Microphone, MicrophoneSlash, Stop } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import { Button } from "./button";
import { Tooltip } from "./tooltip";

type VoiceInputProps = {
  onResult: (text: string) => void;
  onError?: (error: string) => void;
  className?: string;
  disabled?: boolean;
  continuous?: boolean;
  interimResults?: boolean;
  lang?: string;
};

export const VoiceInput = forwardRef<HTMLButtonElement, VoiceInputProps>(
  (
    {
      onResult,
      onError,
      className,
      disabled = false,
      continuous = true,
      interimResults = true,
      lang = "zh-CN",
    },
    ref,
  ) => {
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const recognitionRef = useRef<SpeechRecognition | null>(null);
    const finalTranscriptRef = useRef("");

    // 检查浏览器是否支持语音识别
    useEffect(() => {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      setIsSupported(!!SpeechRecognition);

      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = continuous;
        recognition.interimResults = interimResults;
        recognition.lang = lang;

        recognition.onstart = () => {
          setIsListening(true);
          finalTranscriptRef.current = "";
        };

        recognition.onresult = (event) => {
          let interimTranscript = "";
          let finalTranscript = "";

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }

          if (finalTranscript) {
            finalTranscriptRef.current += finalTranscript;
            onResult(finalTranscriptRef.current);
          }
        };

        recognition.onerror = (event) => {
          console.error("语音识别错误:", event.error);
          setIsListening(false);
          onError?.(event.error);
        };

        recognition.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current = recognition;
      }

      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };
    }, [continuous, interimResults, lang, onResult, onError]);

    const startListening = useCallback(() => {
      if (recognitionRef.current && !isListening) {
        try {
          recognitionRef.current.start();
        } catch (error) {
          console.error("启动语音识别失败:", error);
          onError?.("启动语音识别失败");
        }
      }
    }, [isListening, onError]);

    const stopListening = useCallback(() => {
      if (recognitionRef.current && isListening) {
        recognitionRef.current.stop();
      }
    }, [isListening]);

    const toggleListening = useCallback(() => {
      if (isListening) {
        stopListening();
      } else {
        startListening();
      }
    }, [isListening, startListening, stopListening]);

    if (!isSupported) {
      return (
        <Tooltip content="您的浏览器不支持语音输入功能">
          <Button
            ref={ref}
            type="button"
            size="sm"
            variant="ghost"
            className={cn("px-2 opacity-50", className)}
            disabled={true}
          >
            <MicrophoneSlash />
          </Button>
        </Tooltip>
      );
    }

    return (
      <Tooltip content={isListening ? "停止语音输入" : "开始语音输入"}>
        <Button
          ref={ref}
          type="button"
          size="sm"
          variant={isListening ? "error" : "ghost"}
          className={cn("px-2", className)}
          disabled={disabled}
          onClick={toggleListening}
        >
          {isListening ? <Stop /> : <Microphone />}
        </Button>
      </Tooltip>
    );
  },
);

VoiceInput.displayName = "VoiceInput";

// 类型声明
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

declare global {
  interface Window {
    SpeechRecognition: {
      new (): SpeechRecognition;
    };
    webkitSpeechRecognition: {
      new (): SpeechRecognition;
    };
  }
}